'use client';

import type React from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onSave?: () => void | Promise<void>;
  isLoading?: boolean;
  size?: 'default' | 'large' | 'full';
  showSaveButton?: boolean;
  disabled?: boolean;
  mobileFullScreen?: boolean;
}

export function EditModal({
  isOpen,
  onClose,
  title,
  children,
  onSave,
  isLoading = false,
  size = 'default',
  showSaveButton = true,
  disabled = false,
  mobileFullScreen = false,
}: EditModalProps) {
  const handleSave = async () => {
    if (onSave) {
      await onSave();
    }
  };

  const sizeClasses = {
    default: 'sm:max-w-3xl',
    large: 'sm:max-w-6xl',
    full: 'sm:max-w-[95vw] sm:max-h-[95vh]',
  };

  const mobileClasses = mobileFullScreen
    ? 'max-sm:top-0 max-sm:translate-y-0 max-sm:h-dvh max-sm:max-w-full max-sm:rounded-none max-sm:flex max-sm:flex-col'
    : '';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`${sizeClasses[size]} ${size === 'full' ? 'h-[95vh]' : ''} ${mobileClasses}`}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className={`${size === 'full' ? 'flex-1 overflow-hidden' : ''} ${mobileFullScreen ? 'max-sm:flex-1 max-sm:overflow-y-auto' : ''}`}>
          {children}
        </div>
        {showSaveButton && (
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isLoading || disabled}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
